import axios from 'axios'
import { getBearerToken, removeTokensFromStorage, storeTokens } from 'utils/localStorageUtil'
import { destroyAuthCookie } from 'utils/cookieUtil'
import Project from '../../package.json'
import { normalizeErrorResponse } from './errorUtil'
import { embedFrameHeaders } from './frameUtil'

export const simpleHeaders = () => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'GRID-Version': `${Project.version}`,
  // The API sends no `Cache-Control` of its own, and several of the statuses it
  // returns — 404, and 410 in particular — are heuristically cacheable, so the
  // browser is free to store one and replay it from disk without asking again.
  //
  // A replayed 410 is indistinguishable from a live one to the response
  // interceptor below, which reads it as a killed session and forces a logout.
  // That survives logging back in, so the only escape is clearing the cache.
  // Nothing here benefits from being cached, and responses on this client carry
  // bearer and refresh tokens, which have no business on disk either.
  'Cache-Control': 'no-store',
  // 'Accept-Language': 'ar'
})

/*
 * Setup the authorization header to be used in requests
 */
export const headers = () => {
  const hdrs = simpleHeaders()
  const token = getBearerToken()
  if (token) {
    return { Authorization: `Bearer ${token}`, ...hdrs }
  }
  return hdrs
}

export const appendQueryParams = (urlString, queryParams) => {
  const url = new URL(urlString)
  Object.keys(queryParams).forEach(key => url.searchParams.append(key, queryParams[key]))
  return url
}

const requestInterceptor = config => {
  const hdrs = { ...config.headers, ...embedFrameHeaders() }
  const token = getBearerToken()
  if (token) {
    hdrs.Authorization = `Bearer ${token}`
  }
  return { ...config, headers: hdrs }
}

const REFRESH_URL = '/auth/local/refresh'

// A 401 from these calls means the credentials or one-time code were rejected.
// Refreshing and retrying them loops: the retry 401s again, refresh runs again.
const SESSION_REFRESH_EXEMPT_URLS = ['/auth/sso/exchange', '/auth/google/exchange', '/auth/local/login']

const skipsSessionRefresh = (requestUrl: string) => (
  requestUrl.includes(REFRESH_URL) || SESSION_REFRESH_EXEMPT_URLS.some(url => requestUrl.includes(url))
)

export const forceLogout = () => {
  removeTokensFromStorage()
  destroyAuthCookie()
  window.location.href = '/login'

  // The browser is navigating to /login, so swallow the in-flight request by
  // returning a promise that never settles. Rejecting here would surface the
  // generic "An Error Occurred" screen for a moment before the redirect lands.
  return new Promise(() => {})
}

// Single in-flight refresh shared by every concurrent 401. A view like the Case
// detail fires many requests at once; if each triggered its own refresh we would
// rotate the refresh token multiple times (later ones 410) and log the user out.
// Sharing one refresh lets all callers await the same result and retry with the
// freshly stored bearer token.
let refreshPromise = null

const readStoredRefreshToken = () => {
  const stored = localStorage.getItem('refreshToken')
  return stored ? JSON.parse(stored) : null
}

const requestRefresh = async () => {
  const refreshToken = readStoredRefreshToken()

  // Without a stored token the request can only come back 401 or 410, and a 410
  // is swallowed by forceLogout's never-settling promise — every caller awaiting
  // the refresh would hang there instead of handling the failure. Fail fast.
  if (!refreshToken) {
    throw new Error('Cannot refresh: no refresh token in storage')
  }

  const hdrs = simpleHeaders()
  const refreshHeaders = { 'refresh-token': `${refreshToken}`, ...hdrs }
  const resp = await gridClient.post(REFRESH_URL, {}, { headers: refreshHeaders })

  if (resp.status === 201 && resp.data.bearerToken && resp.data.refreshToken) {
    // Persist both rotated tokens — the refresh token is rotated too, so
    // storing only the bearer token would break the next refresh.
    storeTokens(resp.data)
    return resp.data
  }

  throw resp
}

const refreshTokens = () => {
  if (!refreshPromise) {
    refreshPromise = requestRefresh().finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

// Exposed for callers that need a fresh bearer token *before* the API rejects
// them. Onboarding creates the Customer after the user is already signed in, so
// the bearer token minted at sign-up still carries a `null` customerId and every
// customer-scoped request 403s (a 403 never reaches the 401 refresh path below).
// Rotating the tokens right after the Customer exists is the only way out.
export const refreshAuthTokens = () => refreshTokens()

const responseInterceptor = client => async error => {
  const responseStatus = parseInt(error?.response?.status, 10)
  const requestUrl = error?.config?.url || ''

  // Never refresh the refresh call itself, or an auth call whose 401 is the answer.
  if (responseStatus === 401 && !skipsSessionRefresh(requestUrl)) {
    try {
      await refreshTokens()

      // Drop the stale Authorization header so the request interceptor injects
      // the freshly stored bearer token on retry.
      const retryConfig = { ...error.config, headers: { ...error.config?.headers } }
      delete retryConfig.headers.Authorization

      return await client.request(retryConfig)
    } catch (refreshError) {
      // A 410 from /refresh means the refresh token is expired/killed — send
      // the user back to login (clears storage + cookie).
      if (parseInt(refreshError?.response?.status, 10) === 410) {
        return forceLogout()
      }

      return Promise.reject(
        normalizeErrorResponse(refreshError?.response?.data || error.response.data),
      )
    }
  }

  // Any 410 means the session is expired or tokens were killed server-side.
  if (responseStatus === 410) {
    return forceLogout()
  }

  return Promise.reject(normalizeErrorResponse(error))
}

const gridClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: simpleHeaders(),
})
gridClient.interceptors.request.use(requestInterceptor)
gridClient.interceptors.response.use(res => res, responseInterceptor(gridClient))

const sandboxClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_SANDBOX_API_URL,
  headers: simpleHeaders(),
})
sandboxClient.interceptors.request.use(requestInterceptor)
sandboxClient.interceptors.response.use(res => res, responseInterceptor(sandboxClient))

const sandboxApiKeyClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_SANDBOX_API_URL,
  headers: {
    'x-api-key': process.env.NEXT_PUBLIC_SANDBOX_API_KEY,
    ...simpleHeaders(),
  },
})
sandboxApiKeyClient.interceptors.response.use(res => res, responseInterceptor(sandboxApiKeyClient))

export { gridClient, sandboxClient, sandboxApiKeyClient }
